import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Star, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    source: 'google',
    author_name: '',
    rating: 5,
    text: '',
    date: new Date().toISOString().split('T')[0],
    is_visible: true
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    
    try {
      if (editingReview) {
        await axios.patch(`${API}/admin/reviews/${editingReview.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review updated');
      } else {
        await axios.post(`${API}/admin/reviews`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review added');
      }
      setShowForm(false);
      setEditingReview(null);
      resetForm();
      fetchReviews();
    } catch (error) {
      toast.error('Failed to save review');
    }
  };

  const resetForm = () => {
    setFormData({
      source: 'google',
      author_name: '',
      rating: 5,
      text: '',
      date: new Date().toISOString().split('T')[0],
      is_visible: true
    });
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData(review);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const toggleVisibility = async (review) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/reviews/${review.id}`, 
        { is_visible: !review.is_visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'google': return '🔵';
      case 'yelp': return '🔴';
      case 'facebook': return '🔵';
      default: return '⭐';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={i} size={14} 
        className={i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'} />
    ));
  };

  return (
    <div data-testid="reviews-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Reviews Management</h1>
          <p className="text-[#a8b8d0] text-sm mt-1">
            Manage and display customer reviews from Google, Yelp, and Facebook
          </p>
        </div>
        <button onClick={() => { resetForm(); setEditingReview(null); setShowForm(true); }}
          className="btn-accent inline-flex items-center gap-2">
          <Plus size={20} />
          Add Review
        </button>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#132238] rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              {editingReview ? 'Edit Review' : 'Add Review'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Select value={formData.source} onValueChange={(v) => setFormData({...formData, source: v})}>
                <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
                  <SelectItem value="google" className="text-white">🔵 Google</SelectItem>
                  <SelectItem value="yelp" className="text-white">🔴 Yelp</SelectItem>
                  <SelectItem value="facebook" className="text-white">🔵 Facebook</SelectItem>
                  <SelectItem value="manual" className="text-white">⭐ Manual</SelectItem>
                </SelectContent>
              </Select>
              
              <Input placeholder="Author Name" value={formData.author_name}
                onChange={(e) => setFormData({...formData, author_name: e.target.value})} required
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              
              <div>
                <label className="text-[#a8b8d0] text-sm mb-2 block">Rating</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(num => (
                    <button key={num} type="button" onClick={() => setFormData({...formData, rating: num})}
                      className={`p-2 rounded ${formData.rating >= num ? 'text-yellow-400' : 'text-gray-500'}`}>
                      <Star size={24} className={formData.rating >= num ? 'fill-yellow-400' : ''} />
                    </button>
                  ))}
                </div>
              </div>
              
              <textarea placeholder="Review text" value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})} required rows={4}
                className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white" />
              
              <Input type="date" value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              
              <label className="flex items-center gap-2 text-white">
                <Checkbox checked={formData.is_visible}
                  onCheckedChange={(c) => setFormData({...formData, is_visible: c})}
                  className="border-[#1a4d8c] data-[state=checked]:bg-[#22c55e]" />
                Visible on website
              </label>
              
              <div className="flex gap-4 justify-end pt-4">
                <button type="button" onClick={() => { setShowForm(false); setEditingReview(null); }}
                  className="px-4 py-2 text-[#a8b8d0] hover:text-white">Cancel</button>
                <button type="submit" className="btn-accent">
                  {editingReview ? 'Update' : 'Add'} Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reviews Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reviews.map((review) => (
          <div key={review.id} 
            className={`bg-[#132238] rounded-xl p-4 border ${
              review.is_visible ? 'border-[#1a2d47]' : 'border-red-500/30 opacity-60'
            }`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <span>{getSourceIcon(review.source)}</span>
                <span className="text-white font-medium">{review.author_name}</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggleVisibility(review)}
                  className="p-1 text-[#a8b8d0] hover:text-white">
                  {review.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => handleEdit(review)}
                  className="p-1 text-[#a8b8d0] hover:text-white">
                  <Edit size={14} />
                </button>
                <button onClick={() => handleDelete(review.id)}
                  className="p-1 text-red-400 hover:text-red-300">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            
            <div className="flex gap-0.5 mb-2">
              {renderStars(review.rating)}
            </div>
            
            <p className="text-[#a8b8d0] text-sm line-clamp-3 mb-2">{review.text}</p>
            
            <p className="text-[#a8b8d0] text-xs">{review.date}</p>
          </div>
        ))}
      </div>
      
      {reviews.length === 0 && !loading && (
        <div className="text-center py-12">
          <Star size={48} className="text-[#a8b8d0] mx-auto mb-4" />
          <p className="text-[#a8b8d0]">No reviews yet. Add your first review!</p>
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
